import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { SwipaWordmark } from '@/icons';
import { authApi } from '@/api/auth';
import { extractError } from '@/api/client';

/**
 * v5.3 — Self-service account restore (152-ФЗ + GDPR Art 17 reversibility).
 *
 * Two modes on one page:
 *   - Без `?token=...` в URL → показываем форму "восстановление по email/паролю",
 *     юзер вводит свои данные → backend шлёт письмо со ссылкой.
 *   - С `?token=...` (юзер кликнул ссылку из письма) → автоматом consume'ит
 *     токен и редиректит на login со success-сообщением.
 */
export default function RestoreAccountPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [info, setInfo] = useState('');
  const [error, setError] = useState('');

  // Auto-consume on mount if token present (юзер кликнул ссылку из письма).
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await authApi.confirmAccountRestore(token);
        if (cancelled) return;
        navigate('/auth/login', {
          state: { message: `Аккаунт ${r.username} восстановлен. Войдите как обычно.` },
        });
      } catch (err) {
        if (cancelled) return;
        setError(extractError(err));
      }
    })();
    return () => { cancelled = true; };
  }, [token, navigate]);

  const onRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setInfo('');
    try {
      await authApi.requestAccountRestore(emailOrUsername, password);
      setInfo(
        'Если аккаунт существует и в окне восстановления — ссылка отправлена на email.',
      );
    } catch (err) {
      setError(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  // Token-mode skeleton while confirm-call in flight.
  if (token && !error) {
    return (
      <div className="min-h-screen bg-surface-950 flex items-center justify-center p-4">
        <div className="text-surface-400 text-sm">Восстанавливаем аккаунт...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-950 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <SwipaWordmark size={44} base="#ffffff" accent="#C5243A" />
          <p className="text-surface-400 mt-3 text-sm">Восстановление аккаунта</p>
        </div>
        <form
          onSubmit={onRequest}
          className="bg-surface-900 border border-surface-800 rounded-xl p-6 space-y-4"
        >
          {error && (
            <div className="bg-red-900/40 border border-red-700 text-red-300 text-sm rounded-lg px-3 py-2">
              {error}
            </div>
          )}
          {info && (
            <div className="bg-emerald-900/40 border border-emerald-700 text-emerald-300 text-sm rounded-lg px-3 py-2">
              {info}
            </div>
          )}
          <p className="text-surface-400 text-xs">
            Если ты удалил аккаунт и хочешь его вернуть — введи email/username и пароль.
            Восстановление работает в течение 30 дней после удаления.
          </p>
          <div>
            <label className="block text-surface-300 text-xs font-medium mb-1.5">
              Email или username
            </label>
            <input
              value={emailOrUsername}
              onChange={(e) => setEmailOrUsername(e.target.value)}
              required
              autoComplete="username"
              className="irc-input"
              placeholder="user@example.com или username"
            />
          </div>
          <div>
            <label className="block text-surface-300 text-xs font-medium mb-1.5">
              Пароль
            </label>
            <input
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              type="password"
              autoComplete="current-password"
              className="irc-input"
              placeholder="••••••••"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="w-full btn-primary"
          >
            {submitting ? 'Отправка...' : 'Отправить ссылку на email'}
          </button>
          <div className="text-center text-surface-500 text-xs pt-2">
            <Link to="/auth/login" className="hover:text-brand-400">← Назад на вход</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
